import type {
  OnlinePlayerListEntryPayload,
  OnlinePlayerListPayload,
  PlayerProfilePayload,
  VillageTravelPayload,
} from '../gateway/contracts';

const LEGACY_EXTERNAL_VILLAGE_ID = 7979;

export class VisitedVillageHistory {
  private readonly entriesBySession = new Map<string, Map<number, VisitedVillageEntryPayload>>();

  record(
    sessionToken: string,
    localPlayerId: number,
    traveledPlayerId: number,
    directory: OnlinePlayerListPayload,
  ): boolean {
    if (
      sessionToken.length === 0 ||
      directory.viewerPlayerId !== localPlayerId ||
      traveledPlayerId === localPlayerId
    ) {
      return false;
    }
    const entry = directory.entries.find((candidate) => candidate.playerId === traveledPlayerId);
    if (
      entry === undefined ||
      entry.villageId <= 0 ||
      entry.villageId === LEGACY_EXTERNAL_VILLAGE_ID
    ) {
      return false;
    }
    return this.recordEntry(sessionToken, { ...entry, online: true });
  }

  recordTravel(
    sessionToken: string,
    localPlayerId: number,
    traveledPlayerId: number,
    travel: VillageTravelPayload,
    profile: PlayerProfilePayload,
  ): boolean {
    if (
      sessionToken.length === 0 ||
      traveledPlayerId === localPlayerId ||
      travel.playerId !== traveledPlayerId ||
      profile.playerId !== traveledPlayerId ||
      travel.village.id <= 0 ||
      travel.village.id === LEGACY_EXTERNAL_VILLAGE_ID
    ) {
      return false;
    }
    return this.recordEntry(sessionToken, {
      playerId: profile.playerId,
      username: profile.username,
      status: profile.presentation,
      away: false,
      age: profile.age,
      sex: profile.sex,
      villageId: travel.village.id,
      villageName: travel.village.name,
      villageVisitors: 1,
      city: profile.city,
      country: profile.country,
      score: profile.totalScore,
      pictureId: profile.presentationPictureId,
      theme: profile.theme,
      level: profile.level,
      online: profile.online,
    });
  }

  entries(sessionToken: string): readonly VisitedVillageEntryPayload[] {
    return [...(this.entriesBySession.get(sessionToken)?.values() ?? [])];
  }

  async loadEntries(
    sessionToken: string,
    loadOnlinePlayers: (signal: AbortSignal) => Promise<OnlinePlayerListPayload>,
    loadProfile: (playerId: number, signal: AbortSignal) => Promise<PlayerProfilePayload>,
    signal: AbortSignal,
  ): Promise<readonly VisitedVillageEntryPayload[]> {
    const entries = this.entries(sessionToken);
    if (entries.length === 0) return [];
    const directory = await loadOnlinePlayers(signal);
    signal.throwIfAborted();
    const onlinePlayers = new Map(directory.entries.map((entry) => [entry.playerId, entry]));
    const profiles = new Map<number, Promise<PlayerProfilePayload>>();
    return Promise.all(
      entries.map(async (entry) => {
        const online = onlinePlayers.get(entry.playerId);
        if (online !== undefined) {
          return {
            ...online,
            online: true,
            // A player may own several villages; keep the destination actually visited.
            villageId: entry.villageId,
            villageName:
              online.villageId === entry.villageId ? online.villageName : entry.villageName,
            villageVisitors:
              online.villageId === entry.villageId ? online.villageVisitors : entry.villageVisitors,
          };
        }
        let profileRequest = profiles.get(entry.playerId);
        if (profileRequest === undefined) {
          profileRequest = loadProfile(entry.playerId, signal);
          profiles.set(entry.playerId, profileRequest);
        }
        const profile = await profileRequest;
        signal.throwIfAborted();
        if (profile.playerId !== entry.playerId) {
          throw new Error('The game server returned a profile for a different player.');
        }
        return {
          ...entry,
          username: profile.username,
          status: profile.presentation,
          away: false,
          age: profile.age,
          sex: profile.sex,
          city: profile.city,
          country: profile.country,
          score: profile.totalScore,
          pictureId: profile.presentationPictureId,
          theme: profile.theme,
          level: profile.level,
          online: profile.online,
        };
      }),
    );
  }

  clear(): void {
    this.entriesBySession.clear();
  }

  private recordEntry(sessionToken: string, entry: VisitedVillageEntryPayload): boolean {
    let entries = this.entriesBySession.get(sessionToken);
    if (entries === undefined) {
      entries = new Map();
      this.entriesBySession.set(sessionToken, entries);
    }
    if (entries.has(entry.villageId)) return false;
    entries.set(entry.villageId, entry);
    return true;
  }
}

export type VisitedVillageEntryPayload = OnlinePlayerListEntryPayload &
  Readonly<{ online: boolean }>;
